<template lang="pug">
.feedback(v-if="!loaded")
  .feedback-loading
    Loading

.feedback(v-else)
  h1 Avalie a sua box
  p.order-title {{ orderTitle }}

  .feedback-intro(v-if="!started")
    p Vamos fazer algumas perguntinhas sobre a sua box e os produtos que recebeu.&#32;
      | Respondendo a avaliação, você nos ajuda a melhorar a Box Magenta cada vez mais!

    p Vamos usar as suas respostas e as de todos os outros assinantes para entender o que tem funcionado&#32;
      | e o que podemos melhorar. Elas também poderão ser compartilhadas com as&#32;
      | nossas marcas parceiras para que tenham a oportunidade de melhorar os seus&#32;
      | produtos e entender exatamente o que os assinantes da Magenta gostam e esperam.

    p A melhor coisa é preencher a avaliação só depois de experimentar todos os produtos que recebeu.&#32;
      | Assim você vai poder nos contar sobre como foi sua experiência como um todo.

    p
      b Prometo que responder as perguntas vai ser bem rápido! Vamos&nbsp;começar?

    .feedback-actions
      Button(:onClick="next", label="Começar avaliação")

  .feedback-questions(v-else-if="!sent")
    FeedbackQuestion(
      v-if="currentQuestion",
      :question="currentQuestion",
      v-model="answers[currentQuestionIndex]"
    )

    .feedback-actions(v-if="!sending")
      Button(v-if="started", :onClick="previous", label="Voltar", plain)
      Button(
        v-if="started && hasNext",
        :disabled="!currentQuestionAnswered",
        :onClick="next",
        label="Próximo"
      )
      Button(
        v-if="started && !hasNext",
        :disabled="!currentQuestionAnswered",
        :onClick="sendFeedback",
        label="Enviar avaliação"
      )
    .feedback-actions(v-else)
      Loading

  .feedback-thanks(v-else)
    p Muito obrigado por avaliar a sua box!

    p(v-if="!askForReferral") Pode ter certeza de que as suas respostas são muito importantes&#32;
      | para que a chegada da sua Box Magenta seja sempre o momento&#32;
      | mais esperado do mês!

    p(v-if="askForGoogleReview")
      | Se quiser nos ajudar a conquistar cada vez mais pessoas,&#32;
      b nos avalie no Google e conte um pouco da sua experiência!&#32;
      | Ficaremos muito gratos e assim você nos ajuda a&#32;
      | manter a Box Magenta viva e maravilhosa!

    p(v-if="askForReferral")
      b Não se esqueça de indicar a Box Magenta&#32;
      | a alguém que também goste&#32;
      | de se cuidar e conhecer novos produtos de beleza!

    .feedback-actions
      Button(
        label="Fechar avaliação",
        :class="{ small: askForGoogleReview || askForReferral }"
        :to="`/minha-conta/box/${orderId}`",
        :plain="askForGoogleReview || askForReferral",
        replace
      )
      Button.small(
        v-if="askForGoogleReview",
        label="Avaliar no Google",
        to="https://g.page/r/CeXzedSWK99fEAg/review",
        :internal="false",
        target="_blank",
        rel="noreferrer noopener"
      )
      Button.small(
        v-if="askForReferral",
        label="Indicar pelo WhatsApp",
        :to="referralLink",
        :internal="false",
        target="_blank",
        rel="noreferrer noopener"
      )
</template>
<script setup lang="ts">
import { useFeedbackData } from "./FeedbackData"
import { useGoogleReviewData } from "./GoogleReviewData"
import { useOrderInfoView } from "../SubscriptionBoxOrderInfoView"

import FeedbackQuestion from "./FeedbackQuestion.vue"

definePageMeta({
  requiresAuth: true
})

const {
  loaded,
  started,
  sending,
  sent,
  order,
  orderId,
  questions,
  answers,
  send,
  orderScore,
} = useFeedbackData();

const { askForGoogleReview, markGoogleReviewAsked } =
  useGoogleReviewData(orderScore);

const { orderTitle } = useOrderInfoView(order)

const currentQuestionIndex = ref(0);
const currentQuestion = computed(() => {
  if (questions.value) {
    return questions.value[currentQuestionIndex.value]
  } else {
    return null
  }
});

const isMobile = computed(() => {
  const userAgent = navigator.userAgent || navigator.vendor;

  return /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(userAgent)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(userAgent.substr(0,4))
})

const askForReferral = computed(
  () => orderScore.value == 5 && !askForGoogleReview.value
)

const referralLink = computed(
  () => {
    if (isMobile.value) {
      return "https://api.whatsapp.com/send?text=Acho%20que%20voc%C3%AA%20vai%20gostar%20de%20conhecer%20a%20Box%20Magenta%21%20https%3A%2F%2Fboxmagenta.com.br";
    } else {
      return "https://web.whatsapp.com/send?l=pt-BR&text=Acho%20que%20voc%C3%AA%20vai%20gostar%20de%20conhecer%20a%20Box%20Magenta%21%20https%3A%2F%2Fboxmagenta.com.br";
    }
  }
)

const currentQuestionAnswered = computed(
  () => answers.value[currentQuestionIndex.value]?.score > 0
)

const hasPrevious = computed(
  () => questions.value && currentQuestionIndex.value > 0
)

const hasNext = computed(
  () =>
    questions.value &&
    currentQuestionIndex.value < questions.value.length - 1
)

const previous = () => {
  if (currentQuestionIndex.value == 0) {
    started.value = false;
  } else {
    currentQuestionIndex.value -= 1
  }
}

const next = () => {
  if (!started.value) {
    started.value = true
  } else {
    currentQuestionIndex.value += 1
  }
}

const sendFeedback = async () => {
  await send()

  markGoogleReviewAsked()
}
</script>
<style lang="sass" scoped>
@import "~/assets/styles/variables"

.feedback
  max-width: 680px
  margin: 0 auto
  text-align: left
  padding: 0 14px

  .feedback-loading
    display: flex
    height: 320px
    justify-content: center
    align-items: center

  h1
    margin-bottom: 0

  p.order-title
    margin-top: 0
    font-size: 20px
    font-weight: bold
    color: $magenta

  .feedback-intro
    margin-bottom: 32px

  .feedback-actions
    display: flex
    justify-content: center
    margin-top: 32px
    gap: 8px

    .button.small
      padding: 16px 8px
      font-size: 16px
      text-align: center
</style>
